import { useTranslation } from "react-i18next";import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Badge } from '@/shared/components/ui/badge';
import { StatusIndicator } from '@/modules/wms/components/common/StatusIndicator';
import { warehouseAPI } from '@/shared/services/api';
import {
  Settings,
  Plus,
  Warehouse,
  Thermometer,
  LayoutGrid,
  Layers,
  Edit,
  Trash2,
  ChevronRight,
  MapPin,
  Box,
  AlertTriangle,
  CheckCircle2,
  Snowflake,
  Flame,
  Loader2 } from
'lucide-react';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger } from
'@/shared/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter } from
'@/shared/components/ui/dialog';
import { toast } from 'sonner';

const zoneTypeIcons = {
  cold: { icon: Snowflake, color: 'text-blue-500 bg-blue-50' },
  frozen: { icon: Snowflake, color: 'text-cyan-500 bg-cyan-50' },
  ambient: { icon: Box, color: 'text-green-500 bg-green-50' },
  hazmat: { icon: Flame, color: 'text-red-500 bg-red-50' },
  quarantine: { icon: AlertTriangle, color: 'text-amber-500 bg-amber-50' }
};

export const WarehouseConfig = () => {const { t } = useTranslation("wms");
  const [showZoneDialog, setShowZoneDialog] = useState(false);
  const [showAisleDialog, setShowAisleDialog] = useState(false);
  const [selectedZone, setSelectedZone] = useState(null);
  const [loading, setLoading] = useState(true);
  const [warehouseConfig, setWarehouseConfig] = useState(null);
  const [zones, setZones] = useState([]);
  const [aisles, setAisles] = useState([]);
  const [binTypes, setBinTypes] = useState([]);
  const [warehouseStats, setWarehouseStats] = useState({ totalZones: 0, totalAisles: 0, totalBins: 0, usedBins: 0 });

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [config, zonesData, aislesData, binTypesData] = await Promise.all([
        warehouseAPI.getConfig(),
        warehouseAPI.getZones(),
        warehouseAPI.getAisles(),
        warehouseAPI.getBinTypes()]
        );
        setWarehouseConfig(config);
        setZones(zonesData || []);
        setAisles(aislesData || []);
        setBinTypes(binTypesData || []);

        if (config?.stats) {
          setWarehouseStats(config.stats);
        } else {
          setWarehouseStats({
            totalZones: (zonesData || []).length,
            totalAisles: (zonesData || []).reduce((sum, z) => sum + (z.aisles?.length || 0), 0),
            totalBins: (zonesData || []).reduce((sum, z) => sum + (z.totalBins || 0), 0),
            usedBins: (zonesData || []).reduce((sum, z) => sum + (z.usedBins || 0), 0)
          });
        }
      } catch (error) {
        console.error('Failed to fetch warehouse config:', error);
        toast.error('Failed to load warehouse configuration');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const overallUtilization = warehouseStats.totalBins > 0 ?
  Math.round(warehouseStats.usedBins / warehouseStats.totalBins * 100) :
  0;

  return (
    <div className="space-y-6" data-testid="wms-warehouse-config">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight font-['Space_Grotesk']">{t("warehouseConfiguration", "Warehouse Configuration")}

          </h1>
          <p className="text-muted-foreground mt-1">{t("manageZonesAisles", "Manage zones, aisles, bins, and warehouse layout")}

          </p>
        </div>
        <Button variant="outline" className="gap-2">
          <Settings className="h-4 w-4" />{t("generalSettings", "General Settings")}

        </Button>
      </div>

      {/* Warehouse Overview */}
      <Card className="card-elevated bg-gradient-to-r from-blue-50/50 to-indigo-50/50 dark:from-blue-950/20 dark:to-indigo-950/20">
        <CardContent className="p-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            <div className="flex items-center gap-4">
              <div className="p-4 rounded-xl bg-blue-100 dark:bg-blue-900/50">
                <Warehouse className="h-8 w-8 text-blue-600" />
              </div>
              <div>
                <h2 className="text-2xl font-bold font-['Space_Grotesk']">{warehouseConfig?.name || 'EzERP Main Warehouse'}</h2>
                <p className="text-muted-foreground">{warehouseConfig?.address || 'Loading...'}</p>
                <p className="text-sm text-muted-foreground mt-1">{t("code", "Code:")}{warehouseConfig?.code || 'MAIN-01'}</p>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 lg:gap-8">
              <div className="text-center">
                <p className="text-3xl font-bold text-blue-600">{warehouseStats.totalZones}</p>
                <p className="text-sm text-muted-foreground">{t("zones", "Zones")}</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold text-blue-600">{warehouseStats.totalAisles}</p>
                <p className="text-sm text-muted-foreground">{t("aisles", "Aisles")}</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold text-blue-600">{warehouseStats.totalBins.toLocaleString()}</p>
                <p className="text-sm text-muted-foreground">{t("totalBins", "Total Bins")}</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold text-blue-600">{overallUtilization}%</p>
                <p className="text-sm text-muted-foreground">{t("utilized", "Utilized")}</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs defaultValue="zones" className="space-y-4">
        <TabsList className="bg-muted/50">
          <TabsTrigger value="zones" className="gap-2">
            <LayoutGrid className="h-4 w-4" />{t("zones", "Zones")}

            <Badge variant="secondary" className="ml-1">{zones.length}</Badge>
          </TabsTrigger>
          <TabsTrigger value="aisles" className="gap-2">
            <Layers className="h-4 w-4" />{t("aislesRacks", "Aisles & Racks")}

          </TabsTrigger>
          <TabsTrigger value="bin-types" className="gap-2">
            <Box className="h-4 w-4" />{t("binTypes", "Bin Types")}

          </TabsTrigger>
        </TabsList>

        {/* Zones Tab */}
        <TabsContent value="zones" className="space-y-4">
          <div className="flex justify-end">
            <Button onClick={() => setShowZoneDialog(true)} className="gap-2">
              <Plus className="h-4 w-4" />{t("addZone", "Add Zone")}

            </Button>
          </div>

          {loading ?
          <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              <span className="ml-2 text-muted-foreground">{t("loadingZones", "Loading zones...")}</span>
            </div> :

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {zones.map((zone) => {
              const typeConfig = zoneTypeIcons[zone.type] || zoneTypeIcons.ambient;
              const Icon = typeConfig.icon;
              const utilizationPercent = zone.totalBins > 0 ? Math.round(zone.usedBins / zone.totalBins * 100) : 0;

              return (
                <Card
                  key={zone.id}
                  className="card-elevated hover:shadow-lg transition-shadow cursor-pointer"
                  onClick={() => setSelectedZone(zone)}>
                  
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between mb-4">
                      <div className={`p-2 rounded-lg ${typeConfig.color}`}>
                        <Icon className="h-5 w-5" />
                      </div>
                      <Badge variant="outline">{zone.code}</Badge>
                    </div>
                    
                    <h3 className="font-semibold mb-1">{zone.name}</h3>
                    
                    {zone.temperature.min !== null &&
                    <div className="flex items-center gap-1.5 text-sm text-muted-foreground mb-3">
                        <Thermometer className="h-3.5 w-3.5" />
                        <span>{zone.temperature.min}{t("to", "to")}{zone.temperature.max}{zone.temperature.unit}</span>
                      </div>
                    }

                    <div className="space-y-2 mb-4">
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">{t("capacity", "Capacity")}</span>
                        <span className="font-medium">{zone.usedBins} / {zone.totalBins}{t("bins", "bins")}</span>
                      </div>
                      <div className="h-2 bg-muted rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full ${
                          utilizationPercent > 90 ? 'bg-red-500' :
                          utilizationPercent > 70 ? 'bg-amber-500' : 'bg-green-500'}`
                          }
                          style={{ width: `${utilizationPercent}%` }} />
                        
                      </div>
                    </div>

                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">{zone.aisles.length}{t("aisles", "aisles")}</span>
                      <div className="flex items-center gap-1 text-blue-600">
                        <span>{t("configure", "Configure")}</span>
                        <ChevronRight className="h-4 w-4" />
                      </div>
                    </div>
                  </CardContent>
                </Card>);

            })}
          </div>
          }
        </TabsContent>

        {/* Aisles Tab */}
        <TabsContent value="aisles" className="space-y-4">
          <div className="flex justify-end">
            <Button onClick={() => setShowAisleDialog(true)} className="gap-2">
              <Plus className="h-4 w-4" />{t("addAisle", "Add Aisle")}

            </Button>
          </div>

          <Card className="card-elevated">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border bg-muted/30">
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">{t("aisleId", "Aisle ID")}</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">{t("zone", "Zone")}</th>
                      <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground">{t("racks", "Racks")}</th>
                      <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground">{t("levels", "Levels")}</th>
                      <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground">{t("binslevel", "Bins/Level")}</th>
                      <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground">{t("totalBins", "Total Bins")}</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">{t("status", "Status")}</th>
                      <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">{t("actions", "Actions")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {aisles.map((aisle) =>
                    <tr key={aisle.id} className="border-b border-border/50 hover:bg-muted/30 transition-colors">
                        <td className="py-4 px-4">
                          <span className="font-mono text-sm font-medium">{aisle.id}</span>
                        </td>
                        <td className="py-4 px-4 text-sm">
                          {zones.find((z) => z.id === aisle.zone)?.name || aisle.zone}
                        </td>
                        <td className="py-4 px-4 text-center font-medium">{aisle.racks}</td>
                        <td className="py-4 px-4 text-center">{aisle.levelsPerRack}</td>
                        <td className="py-4 px-4 text-center">{aisle.binsPerLevel}</td>
                        <td className="py-4 px-4 text-center font-medium">{aisle.totalBins}</td>
                        <td className="py-4 px-4">
                          <StatusIndicator status={aisle.status === 'active' ? 'in_stock' : 'issue'} />
                        </td>
                        <td className="py-4 px-4 text-right">
                          <div className="flex gap-1 justify-end">
                            <Button variant="ghost" size="sm">
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="sm" className="text-red-600">
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Bin Types Tab */}
        <TabsContent value="bin-types" className="space-y-4">
          <div className="flex justify-end">
            <Button className="gap-2">
              <Plus className="h-4 w-4" />{t("addBinType", "Add Bin Type")}

            </Button>
          </div>

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {binTypes.map((binType) =>
            <Card key={binType.id} className="card-elevated">
                <CardContent className="p-5">
                  <div className="flex items-center justify-between mb-4">
                    <div
                    className="p-3 rounded-lg"
                    style={{ backgroundColor: `${binType.color}20` }}>
                    
                      <Box className="h-6 w-6" style={{ color: binType.color }} />
                    </div>
                    <Button variant="ghost" size="sm">
                      <Edit className="h-4 w-4" />
                    </Button>
                  </div>
                  <h3 className="font-semibold mb-2">{binType.name}</h3>
                  <div className="space-y-1 text-sm text-muted-foreground">
                    <p>{t("dimensions", "Dimensions:")}{binType.dimensions}</p>
                    <p>{t("maxWeight", "Max Weight:")}{binType.maxWeight}{t("kg", "kg")}</p>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Add Zone Dialog */}
      <Dialog open={showZoneDialog} onOpenChange={setShowZoneDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{t("addNewZone", "Add New Zone")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="text-sm font-medium mb-2 block">{t("zoneName", "Zone Name *")}</label>
                <Input placeholder="e.g., Zone E - Overflow" />
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">{t("zoneCode", "Zone Code *")}</label>
                <Input placeholder="e.g., E" maxLength={2} />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">{t("zoneType", "Zone Type *")}</label>
              <select className="w-full h-10 px-3 border border-input rounded-md bg-background">
                <option value="ambient">{t("ambientStorage", "Ambient Storage")}</option>
                <option value="cold">{t("coldStorage28c", "Cold Storage (2-8\xB0C)")}</option>
                <option value="frozen">{t("frozen20c", "Frozen (-20\xB0C)")}</option>
                <option value="hazmat">{t("hazardousMaterials", "Hazardous Materials")}</option>
                <option value="quarantine">{t("quarantine", "Quarantine")}</option>
              </select>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="text-sm font-medium mb-2 block">{t("minTemperature", "Min Temperature")}</label>
                <Input type="number" placeholder="°C" />
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">{t("maxTemperature", "Max Temperature")}</label>
                <Input type="number" placeholder="°C" />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">{t("specialRequirements", "Special Requirements")}</label>
              <Input placeholder="e.g., Backup cooling, restricted access" />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowZoneDialog(false)}>{t("cancel", "Cancel")}

            </Button>
            <Button onClick={() => setShowZoneDialog(false)}>{t("createZone", "Create Zone")}

            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Aisle Dialog */}
      <Dialog open={showAisleDialog} onOpenChange={setShowAisleDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{t("addNewAisle", "Add New Aisle")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="text-sm font-medium mb-2 block">{t("aisleId", "Aisle ID *")}</label>
                <Input placeholder="e.g., B-03" />
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">{t("zone", "Zone *")}</label>
                <select className="w-full h-10 px-3 border border-input rounded-md bg-background">
                  {zones.map((z) =>
                  <option key={z.id} value={z.id}>{z.name}</option>
                  )}
                </select>
              </div>
            </div>
            <div className="grid gap-4 sm:grid-cols-3">
              <div>
                <label className="text-sm font-medium mb-2 block">{t("racks", "Racks")}</label>
                <Input type="number" placeholder="10" />
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">{t("levelsrack", "Levels/Rack")}</label>
                <Input type="number" placeholder="6" />
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">{t("binslevel", "Bins/Level")}</label>
                <Input type="number" placeholder="4" />
              </div>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowAisleDialog(false)}>{t("cancel", "Cancel")}

            </Button>
            <Button onClick={() => setShowAisleDialog(false)}>{t("createAisle", "Create Aisle")}

            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>);

};